/**
 * Bridges RR3's parent→child prop injection to TanStack's `<Outlet/>`.
 *
 * RR3 layout route components inject props into their matched child via
 * `React.cloneElement(this.props.children, {…})` (e.g. SubjectView passes
 * `subject`/`user` down to StudyGuide). TanStack renders children through
 * `<Outlet/>`, which can't receive cloned props. So `layoutComponent` renders
 * `<InjectedOutlet/>` as the layout's children: when the layout clones it, the
 * injected props land here and are published via context; child route
 * components pick them up in `withRouter` (see useRouterShim).
 */
import * as React from 'react';
import {Outlet} from '@tanstack/react-router';

export const LayoutInjectedPropsContext = React.createContext<Record<string, unknown>>({});

export function InjectedOutlet(props: Record<string, unknown>): React.ReactElement<any> {
  const parent = React.useContext(LayoutInjectedPropsContext);
  // Merge with any outer layout's injected props so nested layouts accumulate.
  const merged = {...parent, ...props};
  return (
    <LayoutInjectedPropsContext.Provider value={merged}>
      <Outlet />
    </LayoutInjectedPropsContext.Provider>
  );
}
